import { useEffect, useRef } from 'react';
import { array, bool, object, string, number, func } from 'prop-types';
import { useInView } from 'react-intersection-observer';

import ProductsGrid from 'components/productsGrid';
import { DEFAULT_LIMIT, updateQuerystringParam } from 'utils/helpers';
import { enterToView, leftFromView } from 'utils/grid';
import { LoadPageTrigger } from 'components/grid/load-page-trigger';
import { Col } from 'antd';
import ProductPlaceholder from 'components/productPlaceholder';
import { getDomainValue } from 'utils/domain';

function PaginatedGrid({
  products = [],
  locale,
  colXS = { span: 12 },
  colMD = { span: 8 },
  colLG = { span: 8 },
  colXL = { span: 8 },
  colXXL = { span: 6 },
  page = 1,
  totalPages,
  handleLoadNextPage,
  withLabel = false,
  hasModal = true,
  currentList,
  camperlab = false,
  withHover = true,
  recalculating = false,
  voucherApplied = false,
  currentListName,
  placeholder,
  updatePage,
  saveLastProduct = false,
}) {
  const {
    ref: refLoadMore,
    inView,
    entry,
  } = useInView({
    threshold: 0,
    rootMargin: '0% 0%',
  });
  const prevInView = useRef(inView);

  const square = getDomainValue({ camper: true, nnormal: false, camperlab: false });

  useEffect(() => {
    prevInView.current = inView;
    if (!inView && entry && page < totalPages - 1) {
      const leftDirection = leftFromView(entry.boundingClientRect.top, entry.boundingClientRect.bottom);
      const newPage = leftDirection === 'top' ? page + 2 : page + 1;
      updateQuerystringParam(window.location.search, 'page', newPage, 1);
      updatePage(newPage);
    }
    /**
     * como puede que se salte el load trigger de en medio, porque al cerrar los filtros se mueva el inview de debajo a encima sin entrar nunca en la pantalla,  lanzamos el fetch siguiente cuando el inView  que cambia de pagina entre en la ventana.
     * Si tiene que hacer fetch o no lo decide el componente padre
     */
    if (inView && entry && page < totalPages - 1) {
      // solo funciona si inView es true porque calcula la direccion de entrada
      const enterFrom = enterToView(entry.boundingClientRect, entry.rootBounds);
      // deberiamos hacer fetch solo si el trigger entra por debajo
      if (enterFrom === 'bottom') {
        const newPage = enterFrom === 'top' ? page - 1 : page + 1;
        handleLoadNextPage(newPage, 'bottom');
      }
    }
  }, [inView]);

  if (placeholder && typeof placeholder === 'number') {
    return (
      <>
        {Array(placeholder)
          .fill()
          .map((n, i) => {
            const isMiddle = i === Math.floor(placeholder / 2);
            return (
              <Col xs={colXS} md={colMD} lg={colLG} xl={colXL} xxl={colXXL} key={`page-${page}-${i}-placeholder`}>
                {isMiddle ?
                  <LoadPageTrigger page={page} onVisible={(newPage) => handleLoadNextPage(newPage, 'middle')} />
                : null}
                <ProductPlaceholder square={square} />
              </Col>
            );
          })}
        <div data-bottom={page} ref={refLoadMore}></div>
      </>
    );
  }

  return (
    <>
      <ProductsGrid
        origin="grid"
        currentList={currentList}
        products={products}
        locale={locale}
        colXS={colXS}
        colMD={colMD}
        colLG={colLG}
        colXL={colXL}
        colXXL={colXXL}
        camperlab={camperlab}
        withHover={withHover}
        recalculating={recalculating}
        voucherApplied={voucherApplied}
        withModal={hasModal}
        withLabel={withLabel}
        currentListName={currentListName}
        loadPage={<LoadPageTrigger page={page} onVisible={(newPage) => handleLoadNextPage(newPage, 'middle')} />}
        saveLastProduct={saveLastProduct}
        page={page}
        squareImg={square}
      />
      <div data-bottom={page} ref={refLoadMore}></div>
    </>
  );
}

PaginatedGrid.propTypes = {
  products: array,
  loading: bool,
  colXS: object,
  colMD: object,
  colLG: object,
  colXL: object,
  colXXL: object,
  locale: string,
  withPlaceholder: bool,
  withHover: bool,
  withLabel: bool,
  defaultLimit: number,
  page: number,
  handleLoadNextPage: func.isRequired,
  currentList: string,
  camperlab: bool,
  recalculating: bool,
  voucherApplied: bool,
  loadingNextPage: bool,
  hasModal: bool,
  currentListName: string,
};

export default PaginatedGrid;
